import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import PasscodeVerification from '@/components/PasscodeVerification';
import { toast } from 'sonner';
import { ArrowLeft, FileText, Trash2, Eye, RefreshCw, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminLogs = () => {
  const navigate = useNavigate();
  const [logFiles, setLogFiles] = useState([]);
  const [selectedLog, setSelectedLog] = useState(null);
  const [logContent, setLogContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [passcodeDialogOpen, setPasscodeDialogOpen] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);
  const [hasPasscode, setHasPasscode] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchLogFiles();
    checkPasscodeStatus();
  }, []);

  const checkPasscodeStatus = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${API}/admin/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHasPasscode(response.data.has_passcode || false);
    } catch (error) {
      console.error('Failed to check passcode status:', error);
    }
  };

  const fetchLogFiles = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${API}/admin/logs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const data = response.data;
      setLogFiles(data.files || []);
      
      // Show informational message if no logs are available
      if (data.message && data.checked_directories) {
        // Debug info intentionally suppressed
      }
    } catch (error) {
      console.error('Failed to fetch log files:', error);
      toast.error('Failed to fetch log files');
    } finally {
      setLoading(false);
    }
  };

  const viewLogFile = async (filename) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${API}/admin/logs/${filename}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedLog(filename);
      setLogContent(response.data.content || '');
      setViewDialogOpen(true);
    } catch (error) {
      console.error('Failed to view log:', error);
      toast.error('Failed to view log file');
    }
  };

  const deleteLogFile = async (filename) => {
    if (!window.confirm(`Are you sure you want to delete ${filename}?`)) {
      return;
    }

    // If passcode is set up, require verification
    if (hasPasscode) {
      setFileToDelete(filename);
      setPasscodeDialogOpen(true);
      return;
    }

    // Otherwise proceed directly
    await performDelete(filename);
  };

  const performDelete = async (filename) => {
    try {
      const token = localStorage.getItem('adminToken');
      await axios.delete(`${API}/admin/logs/${filename}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Log file deleted successfully');
      fetchLogFiles();
    } catch (error) {
      console.error('Failed to delete log:', error);
      toast.error('Failed to delete log file');
    }
  };

  const handlePasscodeVerified = () => {
    setPasscodeDialogOpen(false);
    if (fileToDelete) {
      performDelete(fileToDelete);
      setFileToDelete(null);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Header */}
      <div className="bg-white border-b border-stone-200 py-3 md:py-4 px-4 md:px-6 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate('/admin/dashboard')}
              variant="ghost"
              size="sm"
              className="text-stone-600 hover:text-[#1A4D2E]"
            >
              <ArrowLeft size={18} className="mr-2" />
              Back
            </Button>
            <h1 className="text-xl md:text-3xl font-bold text-[#1A4D2E]" style={{fontFamily: '"Playfair Display", serif'}}>
              System Logs
            </h1>
          </div>
          <Button
            onClick={fetchLogFiles}
            variant="outline"
            size="sm"
            className="text-stone-600 hover:text-[#1A4D2E]"
          >
            <RefreshCw size={18} className="mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-4 md:p-6">
        {/* Info Card */}
        <Card className="p-4 mb-6 bg-blue-50 border-blue-200">
          <div className="flex items-start gap-3">
            <AlertCircle className="text-blue-600 mt-1" size={20} />
            <div className="text-sm text-blue-800">
              <p className="font-semibold mb-1">Log Files Information</p>
              <p>View backend error logs and output logs. Delete old log files to free up space.</p>
              <p className="mt-1 text-xs text-blue-600">Location: /var/log/supervisor/</p>
            </div>
          </div>
        </Card>

        {/* Log Files List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1A4D2E] mx-auto"></div>
            <p className="mt-4 text-stone-600">Loading log files...</p>
          </div>
        ) : logFiles.length === 0 ? (
          <Card className="p-12 text-center">
            <FileText size={48} className="mx-auto text-stone-400 mb-4" />
            <p className="text-stone-900 font-semibold mb-2">No log files available</p>
            <p className="text-stone-600 text-sm max-w-md mx-auto">
              Log files may not be accessible in this deployment environment. 
              This is common on managed hosting platforms where direct file system access is restricted.
            </p>
            <p className="text-stone-500 text-xs mt-4">
              For debugging, please use your hosting platform's log viewer or contact support.
            </p>
          </Card>
        ) : (
          <div className="grid gap-4">
            {logFiles.map((file) => (
              <Card key={file.name} className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 flex-1">
                    <FileText className={file.name.includes('.err') ? 'text-red-500' : 'text-blue-500'} size={24} />
                    <div className="flex-1">
                      <h3 className="font-semibold text-stone-900">{file.name}</h3>
                      <p className="text-sm text-stone-600">
                        Size: {formatFileSize(file.size)} • Modified: {new Date(file.modified).toLocaleString()}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      onClick={() => viewLogFile(file.name)}
                      variant="outline"
                      size="sm"
                      className="text-blue-600 hover:text-blue-700"
                    >
                      <Eye size={16} className="mr-2" />
                      View
                    </Button>
                    <Button
                      onClick={() => deleteLogFile(file.name)}
                      variant="outline"
                      size="sm"
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 size={16} className="mr-2" />
                      Delete
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* View Log Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>Log File: {selectedLog}</DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            <div className="bg-stone-900 text-stone-100 p-4 rounded-lg overflow-auto max-h-[60vh] font-mono text-xs">
              <pre className="whitespace-pre-wrap">{logContent}</pre>
            </div>
            <div className="mt-4 flex justify-end">
              <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Passcode Verification Dialog */}
      <PasscodeVerification
        isOpen={passcodeDialogOpen}
        onClose={() => {
          setPasscodeDialogOpen(false);
          setFileToDelete(null);
        }}
        onVerified={handlePasscodeVerified}
        title="Delete Log File"
        message={`You are about to delete ${fileToDelete}. Please verify your passcode to continue.`}
      />
    </div>
  );
};

export default AdminLogs;
